// frontend/src/pages/NewDocumentPage.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Autocomplete,
  CircularProgress, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio,
  Stepper, Step, StepLabel, Chip, Tooltip,ToggleButton,          
  ToggleButtonGroup 
} from '@mui/material';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PersonIcon from '@mui/icons-material/Person'; 
import BusinessIcon from '@mui/icons-material/Business';  
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

const steps = ['Informações do Documento', 'Seleção de Assinantes'];

const NewDocumentPage = () => {
  const { user } = useAuth();
  const { showSuccess, showError, showWarning } = useNotification();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [workspace, setWorkspace] = useState(() => user?.empresa ? 'company' : 'personal');
  const [descricao, setDescricao] = useState('');
  const [arquivo, setArquivo] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [signerOptions, setSignerOptions] = useState([]);
  const [loadingSigners, setLoadingSigners] = useState(false);
  const [allAreas, setAllAreas] = useState([]);
  const [loadingInitialData, setLoadingInitialData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [favoriteGroups, setFavoriteGroups] = useState([]);
  const [isSaveFavoriteOpen, setIsSaveFavoriteOpen] = useState(false);
  const [favoriteGroupName, setFavoriteGroupName] = useState('');

  const handleNext = () => {
    if (activeStep === 0 && (!descricao || !arquivo || !selectedArea)) {
      showWarning('Por favor, preencha a Descrição, a Área e selecione um arquivo PDF.');
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingInitialData(true);
      try {
        const [areasResponse, favoritesResponse] = await Promise.all([
          api.get('/areas/'),
          api.get('/grupos-favoritos/')
        ]);
        setAllAreas(areasResponse.data);
        setFavoriteGroups(favoritesResponse.data);
      } catch (error) { console.error("Falha ao buscar dados", error); }
      finally { setLoadingInitialData(false); }
    };
    fetchData();
  }, []);

  const fetchSigners = async (searchTerm) => {
    if (!searchTerm) {
        setSignerOptions([]);
        return;
      }
      setLoadingSigners(true);
      try {
        const response = await api.get(`/usuarios/?search=${searchTerm}`);
        setSignerOptions(response.data);
      } catch (error) {
        console.error("Falha ao buscar assinantes", error);
      } finally {
        setLoadingSigners(false);
      }
  };
  const debouncedFetchSigners = useMemo(() => debounce(fetchSigners, 500), []);
  const handleFileChange = (event) => setArquivo(event.target.files[0]);
  const handleLoadFavorite = (group) => {
    if (group && group.assinantes) {
        setSelectedUsers(group.assinantes);
      }
  };
  const handleSaveFavorite = async () => {
    if (!favoriteGroupName.trim() || selectedUsers.length === 0) {
        showWarning('Dê um nome ao grupo e selecione ao menos um assinante.');
        return;
      }
      try {
        const assinante_ids = selectedUsers.map(u => u.id);
        await api.post('/grupos-favoritos/', { nome: favoriteGroupName, assinante_ids: assinante_ids, });
        setIsSaveFavoriteOpen(false);
        setFavoriteGroupName('');
        const response = await api.get('/grupos-favoritos/');
        setFavoriteGroups(response.data);
        showSuccess('Grupo favorito salvo com sucesso!');
      } catch (error) { console.error('Erro ao salvar grupo', error); showError('Erro ao salvar grupo favorito.'); }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (selectedUsers.length === 0) {
        showWarning('Selecione ao menos um assinante para o documento.');
        return;
    }
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('descricao', descricao);
    formData.append('caminho_pdf_original', arquivo);
    formData.append('data_recebido', new Date().toISOString());
    formData.append('area_envio', selectedArea.id);
    const pedidosAssinatura = selectedUsers.map(user => ({ assinante_id: user.id }));
    formData.append('pedidos_assinatura', JSON.stringify(pedidosAssinatura));
    if (workspace === 'company' && user?.empresa) {
      formData.append('empresa', user.empresa.id);
    }
    try {
      await api.post('/documentos/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showSuccess('Documento enviado com sucesso!');
      navigate('/');
    } catch (error) {
      const errorDetails = error.response?.data?.message || JSON.stringify(error.response?.data);
      console.error('Erro ao criar documento!', error.response?.data);
      showError(`Erro ao criar documento. Detalhes: ${errorDetails}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
            {user?.empresa && (
              <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  value={workspace}
                  exclusive
                  onChange={(_, val) => val && setWorkspace(val)}
                >
                  <ToggleButton
                    value="personal"
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: '#1565C0',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#1565C0' },
                      }
                    }}
                  >
                    <PersonIcon sx={{ mr: .5 }} /> Espaço Pessoal
                  </ToggleButton>
                  <ToggleButton
                    value="company"
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: '#1565C0',
                        color: '#fff',
                        '&:hover': { backgroundColor: '#1565C0' },
                      }
                    }}
                  >
                    <BusinessIcon sx={{ mr: .5 }} /> Empresa: {user.empresa.nome}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            <TextField fullWidth label="Descrição / Título do Documento" value={descricao} onChange={(e) => setDescricao(e.target.value)} required />
            <Autocomplete options={allAreas} getOptionLabel={(option) => option.nome || ''} value={selectedArea} onChange={(event, newValue) => setSelectedArea(newValue)} renderInput={(params) => <TextField {...params} label="Área de Envio" required />} />
            <Box>
              <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                Selecionar PDF
                <input type="file" accept="application/pdf" hidden onChange={handleFileChange} />
              </Button>
              {arquivo && <Chip label={arquivo.name} onDelete={() => setArquivo(null)} sx={{ ml: 2 }} />}
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
             <Autocomplete options={favoriteGroups} getOptionLabel={(option) => option.nome || ''} onChange={(event, group) => handleLoadFavorite(group)} renderInput={(params) => <TextField {...params} label="Ou Carregue um Grupo Favorito" />} />
             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Autocomplete
                  multiple sx={{ flexGrow: 1 }} options={signerOptions} filterOptions={(x) => x}
                  getOptionLabel={(option) => option.nome_completo || ''} isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedUsers}
                  onChange={(event, newValue) => {
                    setSignerOptions(newValue ? [...newValue, ...signerOptions.filter(option => !newValue.some(val => val.id === option.id))] : signerOptions);
                    setSelectedUsers(newValue);
                  }}
                  onInputChange={(event, newInputValue) => debouncedFetchSigners(newInputValue)}
                  loading={loadingSigners}
                  renderInput={(params) => (
                    <TextField {...params} label="Assinantes: Buscar por Nome, CPF, etc." required={selectedUsers.length === 0}
                      InputProps={{ ...params.InputProps, endAdornment: (<>{loadingSigners ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>), }}
                    />
                  )}
                />
                <Tooltip title="Salvar como favorito">
                  <IconButton onClick={() => setIsSaveFavoriteOpen(true)} disabled={selectedUsers.length === 0}>
                    <StarBorderIcon />
                  </IconButton>
                </Tooltip>
             </Box>
          </Box>
        );
      default:
        return 'Passo desconhecido';
    }
  };

  if (loadingInitialData) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} elevation={3} sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Novo Pedido de Assinatura</Typography>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => ( <Step key={label}><StepLabel>{label}</StepLabel></Step> ))}
      </Stepper>
      
      {getStepContent(activeStep)}
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 1 }}>
        {activeStep > 0 && ( <Button onClick={handleBack}>Voltar</Button> )}
        
        {activeStep < steps.length - 1 && ( <Button variant="contained" onClick={handleNext}>Próximo</Button> )}

        {activeStep === steps.length - 1 && (
          <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
            {isSubmitting ? 'Enviando...' : 'Enviar para Assinatura'}
          </Button>
        )}
      </Box>

      <Dialog open={isSaveFavoriteOpen} onClose={() => setIsSaveFavoriteOpen(false)}>
        <DialogTitle>Salvar Grupo de Assinantes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do Grupo Favorito"
            type="text"
            fullWidth
            variant="standard"
            value={favoriteGroupName}
            onChange={(e) => setFavoriteGroupName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSaveFavoriteOpen(false)}>Cancelar</Button>
          <Button onClick={handleSaveFavorite}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default NewDocumentPage;